import { ReactNode, useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { InputAdornment } from "@mui/material";
import {
	coreToast,
	CoreDatePicker,
	CoreButton,
	CoreTextField,
	CoreLabelText,
	CoreCopyButton,
	CoreTextFieldProps,
	CoreInfoIndicator,
	CoreLabelTextSkeleton,
	CoreTextFieldSkeleton,
} from "@verity/components/core";
import { ThemeWrapper, VerityInstituteSettings } from "@verity/components";
import { changeTimeOnly } from "../../../helpers/time";
import apiClient from "../../../helpers/api-client";
import { canAccessResource } from "../../../helpers/privilege";
import { PortalControllerContext } from "../../context";
import i18n from "../../../i18n";
import PageCard from "../page-card";
import styles from "./InstitutionInformation.module.css";
import VisualizerBanner from "../visualizer-banner";
import InfoIcon from "../../../images/settings/info.svg?react";
import DemoCourseCard, { CLEANUP_FREQUENCY, CleanupFrequency, normalizeCutoffDate } from "./DemoCourse";

const MAX_INT32_VALUE = 2147483647;

function computeDisplayedLiveUsage(storedUsage: number, storedOneTimeHours = 0) {
	const updatedUsage = Math.max(storedUsage - storedOneTimeHours, 0);
	const updatedOneTimeHours = storedOneTimeHours - (storedUsage - updatedUsage);
	return [updatedUsage, updatedOneTimeHours === 0 ? 0 : updatedOneTimeHours];
}

export function InfoRow({
	header,
	description,
	helperText,
	createControl,
	isLoading = false,
}: {
	header: ReactNode;
	description: ReactNode;
	helperText?: ReactNode;
	createControl: (id: string) => ReactNode;
	isLoading?: boolean;
}) {
	const controlId = useId();
	return (
		<tr>
			<th>
				{isLoading ? (
					<CoreLabelTextSkeleton />
				) : (
					<CoreLabelText
						header={
							<>
								{header}
								{helperText && (
									<div
										className={styles.rowIndicator}
										onClick={(e) => {
											// Added this so clicking on the coreInfo doesn't cause the control to be clicked too
											e.preventDefault();
											e.stopPropagation();
										}}
									>
										<CoreInfoIndicator hint={helperText} size={20} />
									</div>
								)}
							</>
						}
						htmlFor={controlId}
					>
						{description}
					</CoreLabelText>
				)}
			</th>
			<td>{isLoading ? <CoreTextFieldSkeleton /> : createControl(controlId)}</td>
		</tr>
	);
}

function CopyOnlyTextField({
	id,
	value,
	copyButtonAriaLabel,
}: Pick<CoreTextFieldProps, "id" | "value"> & { copyButtonAriaLabel?: string }) {
	return (
		<CoreTextField
			id={id}
			size="large"
			autoComplete="off"
			value={value}
			fullWidth
			InputProps={{
				readOnly: true,
				endAdornment: (
					<InputAdornment position="end" style={{ paddingRight: "6px" }}>
						<CoreCopyButton text={value?.toString() ?? ""} aria-label={copyButtonAriaLabel} />
					</InputAdornment>
				),
			}}
		/>
	);
}

export function InstitutionInformation() {
	const { userPrivileges, portalContext } = useContext(PortalControllerContext);
	const { lms, instituteId, instituteUuid } = portalContext;
	const canEditVeritySettings = canAccessResource(
		{ privileges: userPrivileges },
		{
			privilege: "INSTITUTE/FEATURE",
			action: "UPDATE",
			resourceScope: { instituteId: instituteUuid, resourceId: instituteUuid },
		},
	);

	const [instituteName, setInstituteName] = useState<string>();
	const [instituteUrl, setInstituteUrl] = useState<string>();
	const [instituteLms, setInstituteLms] = useState<string>();
	const [initialVerityInstituteSettings, setInitialVerityInstituteSettings] = useState<
		VerityInstituteSettings | undefined
	>();
	const [loading, setLoading] = useState(true);

	const [liveProctoringResetableHours, setLiveProctoringResetableHours] = useState<string>("");
	const [liveProctoringHoursResetDate, setLiveProctoringHoursResetDate] = useState<Date | undefined>(undefined);
	const [liveProctoringOneTimeHours, setLiveProctoringOneTimeHours] = useState<string>("");
	const [storedLiveProctoringUsage, setStoredLiveProctoringUsage] = useState(0);
	const [livePopInResetableHours, setLivePopInResetableHours] = useState<string>("");
	const [livePopInHoursResetDate, setLivePopInHoursResetDate] = useState<Date | undefined>(undefined);
	const [livePopInOneTimeHours, setLivePopInOneTimeHours] = useState<string>("");
	const [storedLivePopInUsage, setStoredLivePopInUsage] = useState(0);
	const [salesforceID, setSalesForceID] = useState<string | undefined>(undefined);
	const [cleanupEnabled, setCleanupEnabled] = useState<boolean>(false);
	const [cutoffDate, setCutoffDate] = useState<Date>(new Date());
	const [cleanupFrequency, setCleanupFrequency] = useState<CleanupFrequency>("weekly");

	const [liveProctoringUsage, initialDisplayedLiveProctoringOneTimeHours] = computeDisplayedLiveUsage(
		storedLiveProctoringUsage,
		initialVerityInstituteSettings?.liveProctoringOneTimeCredits,
	);
	const [livePopInUsage, initialDisplayedLivePopInOneTimeHours] = computeDisplayedLiveUsage(
		storedLivePopInUsage,
		initialVerityInstituteSettings?.livePopInOneTimeCredits,
	);

	const lastCleanupAt = useRef<Date | undefined>(undefined);

	const resetEditableFields = useCallback(() => {
		setLiveProctoringResetableHours(
			initialVerityInstituteSettings?.liveProctoringResetableCredits == null
				? ""
				: String(initialVerityInstituteSettings.liveProctoringResetableCredits),
		);
		setLiveProctoringHoursResetDate(
			initialVerityInstituteSettings?.liveProctoringCreditsResetDate
				? new Date(initialVerityInstituteSettings.liveProctoringCreditsResetDate)
				: undefined,
		);
		setLiveProctoringOneTimeHours(
			initialDisplayedLiveProctoringOneTimeHours ? initialDisplayedLiveProctoringOneTimeHours.toFixed(2) : "0",
		);
		setLivePopInResetableHours(
			initialVerityInstituteSettings?.livePopInResetableCredits == null
				? ""
				: String(initialVerityInstituteSettings.livePopInResetableCredits),
		);
		setLivePopInHoursResetDate(
			initialVerityInstituteSettings?.livePopInCreditsResetDate
				? new Date(initialVerityInstituteSettings.livePopInCreditsResetDate)
				: undefined,
		);
		setLivePopInOneTimeHours(
			initialDisplayedLivePopInOneTimeHours ? initialDisplayedLivePopInOneTimeHours.toFixed(2) : "0",
		);
		setSalesForceID(initialVerityInstituteSettings?.salesforceId ?? "");
		setCleanupEnabled(initialVerityInstituteSettings?.cleanupEnabled ?? false);
		const initialDate: Date = initialVerityInstituteSettings?.cutoffDate
			? new Date(initialVerityInstituteSettings.cutoffDate)
			: new Date();
		initialDate.setHours(23, 59, 59, 999);
		setCutoffDate(initialDate);
		setCleanupFrequency(
			initialVerityInstituteSettings?.cleanupFrequency &&
				CLEANUP_FREQUENCY.includes(initialVerityInstituteSettings.cleanupFrequency as CleanupFrequency)
				? (initialVerityInstituteSettings.cleanupFrequency as CleanupFrequency)
				: "weekly",
		);
		lastCleanupAt.current = initialVerityInstituteSettings?.lastCleanupAt
			? new Date(initialVerityInstituteSettings.lastCleanupAt)
			: undefined;
	}, [
		initialVerityInstituteSettings,
		initialDisplayedLiveProctoringOneTimeHours,
		initialDisplayedLivePopInOneTimeHours,
	]);

	const fetchAndUpdateData = useCallback(async () => {
		if (instituteId) {
			try {
				setLoading(true);
				const institute = await apiClient.institute.get(instituteId);
				const info = await apiClient.institute.getAccessControlInfo(institute.uuid);

				setInstituteName(institute.name);
				setInstituteUrl(institute.origin ?? "");

				setInstituteLms(lms);
				if (info.platform) {
					if (info.platform === "canvas") {
						setInstituteLms("Canvas");
					} else if (info.platform === "desire2learn") {
						setInstituteLms("D2L");
					} else if (info.platform === "moodle") {
						setInstituteLms("Moodle");
					} else if (info.platform === "BlackboardLearn") {
						setInstituteLms("Blackboard");
					}
				}
				const settings = await apiClient.institute.getVeritySettings();
				setInitialVerityInstituteSettings(settings);
				setLoading(false);

				setStoredLiveProctoringUsage(await apiClient.institute.getLiveProctoringUsage(institute.uuid));
				setStoredLivePopInUsage(await apiClient.institute.getLivePopInUsage(institute.uuid));
				setLoading(false);
			} catch (e) {
				console.error(e);
				setLoading(false);
			}
		}
	}, []);

	useEffect(() => {
		void fetchAndUpdateData();
	}, [instituteId]);

	useEffect(resetEditableFields, [resetEditableFields]);

	const currDate = new Date();
	const minDate = changeTimeOnly(new Date(currDate.getTime() + 24 * 60 * 60 * 1000), 0, 0);

	const parsedLiveProctoringResetableHours = liveProctoringResetableHours
		? Number(liveProctoringResetableHours)
		: undefined;
	const liveProctoringResetableHoursValueError =
		parsedLiveProctoringResetableHours &&
		(isNaN(parsedLiveProctoringResetableHours) || parsedLiveProctoringResetableHours < 0);
	const liveProctoringResetableHoursTooLargeError =
		parsedLiveProctoringResetableHours && parsedLiveProctoringResetableHours > MAX_INT32_VALUE;
	const liveProctoringResetableHoursRequiredError = liveProctoringHoursResetDate && !liveProctoringResetableHours;

	const liveProctoringHoursResetDateRequiredError = liveProctoringResetableHours && !liveProctoringHoursResetDate;
	const liveProctoringHoursResetDatePastError = liveProctoringHoursResetDate && liveProctoringHoursResetDate < currDate;

	const parsedLiveProctoringOneTimeHours = Number(liveProctoringOneTimeHours);
	const liveProctoringOneTimeHoursValueError =
		!liveProctoringOneTimeHours || isNaN(parsedLiveProctoringOneTimeHours) || parsedLiveProctoringOneTimeHours < 0;

	const parsedLivePopInResetableHours = livePopInResetableHours ? Number(livePopInResetableHours) : undefined;
	const livePopInResetableHoursValueError =
		parsedLivePopInResetableHours && (isNaN(parsedLivePopInResetableHours) || parsedLivePopInResetableHours < 0);
	const livePopInResetableHoursTooLargeError =
		parsedLivePopInResetableHours && parsedLivePopInResetableHours > MAX_INT32_VALUE;
	const livePopInResetableHoursRequiredError = livePopInHoursResetDate && !livePopInResetableHours;

	const livePopInHoursResetDateRequiredError = livePopInResetableHours && !livePopInHoursResetDate;
	const livePopInHoursResetDatePastError = livePopInHoursResetDate && livePopInHoursResetDate < currDate;

	const parsedLivePopInOneTimeHours = Number(livePopInOneTimeHours);
	const livePopInOneTimeHoursValueError =
		!livePopInOneTimeHours || isNaN(parsedLivePopInOneTimeHours) || parsedLivePopInOneTimeHours < 0;

	return (
		<ThemeWrapper scopedCssBaselineProps={{ className: styles.page }}>
			<div className={styles.pageContainer}>
				<div className={styles.visualizerBanner}>
					<VisualizerBanner />
				</div>
				{canEditVeritySettings && (
					<div className={styles.pageActions}>
						<CoreButton color="cancel" onClick={resetEditableFields} disabled={loading}>
							{i18n.t("portal.components.settingsPageTSX.discardChange")}
						</CoreButton>
						<CoreButton
							onClick={async () => {
								if (instituteId) {
									setLoading(true);
									try {
										const storedLiveProctoringOneTimeHours =
											parsedLiveProctoringOneTimeHours -
											Number(initialDisplayedLiveProctoringOneTimeHours.toFixed(2)) +
											(initialVerityInstituteSettings?.liveProctoringOneTimeCredits ?? 0);
										const storedLivePopInOneTimeHours =
											parsedLivePopInOneTimeHours -
											Number(initialDisplayedLivePopInOneTimeHours.toFixed(2)) +
											(initialVerityInstituteSettings?.livePopInOneTimeCredits ?? 0);
										await apiClient.admin.updateVerityInstituteSettings(instituteId, {
											liveProctoringResetableCredits: parsedLiveProctoringResetableHours ?? null,
											liveProctoringCreditsResetDate: liveProctoringHoursResetDate?.toISOString() ?? null,
											liveProctoringOneTimeCredits: storedLiveProctoringOneTimeHours,
											livePopInResetableCredits: parsedLivePopInResetableHours ?? null,
											livePopInCreditsResetDate: livePopInHoursResetDate?.toISOString() ?? null,
											livePopInOneTimeCredits: storedLivePopInOneTimeHours,
											salesforceId: salesforceID,
											cleanupEnabled: cleanupEnabled,
											cleanupFrequency: cleanupEnabled ? cleanupFrequency : null,
											cutoffDate: cleanupEnabled ? normalizeCutoffDate(cutoffDate) : null,
										});
										coreToast.success(i18n.t("portal.components.settings.institutionInformationTSX.saveSuccess"));
										await fetchAndUpdateData();
									} catch (e) {
										console.error(e);
										coreToast.error(i18n.t("portal.components.settings.institutionInformationTSX.saveError"));
									} finally {
										setLoading(false);
									}
								}
							}}
							disabled={
								loading ||
								liveProctoringResetableHoursValueError ||
								liveProctoringResetableHoursTooLargeError ||
								liveProctoringResetableHoursRequiredError ||
								liveProctoringHoursResetDateRequiredError ||
								liveProctoringHoursResetDatePastError ||
								liveProctoringOneTimeHoursValueError ||
								livePopInResetableHoursValueError ||
								livePopInResetableHoursTooLargeError ||
								livePopInResetableHoursRequiredError ||
								livePopInHoursResetDateRequiredError ||
								livePopInHoursResetDatePastError ||
								livePopInOneTimeHoursValueError
							}
						>
							{i18n.t("portal.components.settingsPageTSX.saveChange")}
						</CoreButton>
					</div>
				)}
				<div className={styles.pageCardWrapper}>
					<PageCard
						icon={<InfoIcon />}
						title={i18n.t("portal.components.settings.institutionInformationTSX.header")}
						titleVariant="h1"
						className={styles.pageCard}
					>
						<div className={styles.pageCardBody}>
							<table className={styles.infoTable} role="none">
								<InfoRow
									header={i18n.t("portal.components.settings.institutionInformationTSX.institutionName")}
									description={i18n.t(
										"portal.components.settings.institutionInformationTSX.institutionNameDescription",
									)}
									createControl={(id) => (
										<CopyOnlyTextField
											id={id}
											value={instituteName ?? ""}
											copyButtonAriaLabel={i18n.t(
												"portal.components.settings.institutionInformationTSX.institutionNameAriaLabel",
											)}
										/>
									)}
									isLoading={loading}
								/>
								<InfoRow
									header={i18n.t("portal.components.settings.institutionInformationTSX.productName")}
									description={i18n.t("portal.components.settings.institutionInformationTSX.productNameDescription")}
									createControl={(id) => (
										<CopyOnlyTextField
											id={id}
											value={i18n.t("portal.components.settings.institutionInformationTSX.productNameVerity")}
											copyButtonAriaLabel={i18n.t(
												"portal.components.settings.institutionInformationTSX.productNameAriaLabel",
											)}
										/>
									)}
									isLoading={loading}
								/>
								{instituteLms && (
									<InfoRow
										header={i18n.t("portal.components.settings.institutionInformationTSX.institutionUrl")}
										description={i18n.t(
											"portal.components.settings.institutionInformationTSX.institutionUrlDescription",
										)}
										createControl={(id) => (
											<CopyOnlyTextField
												id={id}
												value={instituteUrl ?? ""}
												copyButtonAriaLabel={i18n.t(
													"portal.components.settings.institutionInformationTSX.institutionUrlAriaLabel",
												)}
											/>
										)}
										isLoading={loading}
									/>
								)}
								<InfoRow
									header={i18n.t("portal.components.settings.institutionInformationTSX.lmsName")}
									description={i18n.t("portal.components.settings.institutionInformationTSX.lmsNameDescription")}
									createControl={(id) => (
										<CopyOnlyTextField
											id={id}
											value={instituteLms ?? ""}
											copyButtonAriaLabel={i18n.t(
												"portal.components.settings.institutionInformationTSX.lmsNameAriaLabel",
											)}
										/>
									)}
									isLoading={loading}
								/>
								<InfoRow
									header={i18n.t("portal.components.settings.institutionInformationTSX.institutionId")}
									description={i18n.t("portal.components.settings.institutionInformationTSX.institutionIdDescription")}
									createControl={(id) => (
										<CopyOnlyTextField
											id={id}
											value={instituteUuid ?? ""}
											copyButtonAriaLabel={i18n.t(
												"portal.components.settings.institutionInformationTSX.institutionIdAriaLabel",
											)}
										/>
									)}
									isLoading={loading}
								/>
								{canEditVeritySettings && (
									<InfoRow
										header={i18n.t("portal.components.settings.institutionInformationTSX.salesforceId")}
										description={i18n.t("portal.components.settings.institutionInformationTSX.salesforceIdDescription")}
										createControl={(id) => (
											<CoreTextField
												id={id}
												value={salesforceID}
												autoComplete="off"
												fullWidth
												onChange={(e) => setSalesForceID(e.target.value)}
												label={i18n.t("portal.components.settings.institutionInformationTSX.salesforceIdPlaceholder")}
											/>
										)}
										isLoading={loading}
									/>
								)}
								{!initialVerityInstituteSettings?.liteMode && (
									<>
										{initialVerityInstituteSettings?.allowLiveProctoring &&
											(liveProctoringHoursResetDate || canEditVeritySettings) && (
												<>
													<InfoRow
														header={i18n.t("portal.components.settings.institutionInformationTSX.liveResetableHours")}
														description={i18n.t(
															"portal.components.settings.institutionInformationTSX.liveResetableHoursDescription",
														)}
														createControl={(id) =>
															canEditVeritySettings ? (
																<CoreTextField
																	id={id}
																	value={liveProctoringResetableHours}
																	type="number"
																	autoComplete="off"
																	fullWidth
																	onChange={(e) => setLiveProctoringResetableHours(e.target.value)}
																	error={
																		liveProctoringResetableHoursRequiredError
																			? i18n.t(
																					"portal.components.settings.institutionInformationTSX.liveResetableHoursRequired",
																				)
																			: liveProctoringResetableHoursValueError
																				? i18n.t(
																						"portal.components.settings.institutionInformationTSX.liveResetableHoursValueError",
																					)
																				: liveProctoringResetableHoursTooLargeError
																					? i18n.t(
																							"portal.components.settings.institutionInformationTSX.liveResetableHoursTooLargeError",
																						)
																					: undefined
																	}
																/>
															) : (
																<CopyOnlyTextField
																	id={id}
																	value={liveProctoringResetableHours}
																	copyButtonAriaLabel={i18n.t(
																		"portal.components.settings.institutionInformationTSX.liveResetableHoursAriaLabel",
																	)}
																/>
															)
														}
														isLoading={loading}
													/>
													<InfoRow
														header={i18n.t("portal.components.settings.institutionInformationTSX.liveUsage")}
														description={i18n.t(
															"portal.components.settings.institutionInformationTSX.liveUsageDescription",
														)}
														helperText={i18n.t("portal.components.settings.institutionInformationTSX.liveUsageHint")}
														createControl={(id) => (
															<CopyOnlyTextField
																id={id}
																value={liveProctoringUsage.toFixed(2)}
																copyButtonAriaLabel={i18n.t(
																	"portal.components.settings.institutionInformationTSX.liveUsageAriaLabel",
																)}
															/>
														)}
														isLoading={loading}
													/>
													<InfoRow
														header={i18n.t("portal.components.settings.institutionInformationTSX.liveHoursResetDate")}
														description={i18n.t(
															"portal.components.settings.institutionInformationTSX.liveHoursResetDateDescription",
														)}
														createControl={(id) => (
															<CoreDatePicker
																value={liveProctoringHoursResetDate}
																readOnly={!canEditVeritySettings}
																minDate={canEditVeritySettings ? minDate : undefined}
																onChange={
																	canEditVeritySettings
																		? (date) => setLiveProctoringHoursResetDate(date ?? undefined)
																		: undefined
																}
																slotProps={{
																	textField: { id, InputProps: { sx: { width: 165 } } },
																}}
																error={
																	canEditVeritySettings
																		? liveProctoringHoursResetDateRequiredError
																			? i18n.t(
																					"portal.components.settings.institutionInformationTSX.liveHoursResetDateRequired",
																				)
																			: liveProctoringHoursResetDatePastError
																				? i18n.t(
																						"portal.components.settings.institutionInformationTSX.liveHoursResetDatePast",
																					)
																				: undefined
																		: undefined
																}
															/>
														)}
														isLoading={loading}
													/>
													<InfoRow
														header={i18n.t("portal.components.settings.institutionInformationTSX.liveOneTimeHours")}
														description={i18n.t(
															"portal.components.settings.institutionInformationTSX.liveOneTimeHoursDescription",
														)}
														helperText={i18n.t(
															"portal.components.settings.institutionInformationTSX.liveOneTimeHoursHint",
														)}
														createControl={(id) =>
															canEditVeritySettings ? (
																<CoreTextField
																	id={id}
																	value={liveProctoringOneTimeHours}
																	type="number"
																	autoComplete="off"
																	fullWidth
																	onChange={(e) => setLiveProctoringOneTimeHours(e.target.value)}
																	error={
																		liveProctoringOneTimeHoursValueError
																			? i18n.t(
																					"portal.components.settings.institutionInformationTSX.liveOneTimeHoursError",
																				)
																			: undefined
																	}
																/>
															) : (
																<CopyOnlyTextField
																	id={id}
																	value={liveProctoringOneTimeHours}
																	copyButtonAriaLabel={i18n.t(
																		"portal.components.settings.institutionInformationTSX.liveOneTimeHoursAriaLabel",
																	)}
																/>
															)
														}
														isLoading={loading}
													/>
												</>
											)}
										{initialVerityInstituteSettings?.allowLivePopIn &&
											(livePopInHoursResetDate || canEditVeritySettings) && (
												<>
													<InfoRow
														header={i18n.t("portal.components.settings.institutionInformationTSX.popInResetableHours")}
														description={i18n.t(
															"portal.components.settings.institutionInformationTSX.popInResetableHoursDescription",
														)}
														createControl={(id) =>
															canEditVeritySettings ? (
																<CoreTextField
																	id={id}
																	value={livePopInResetableHours}
																	type="number"
																	autoComplete="off"
																	fullWidth
																	onChange={(e) => setLivePopInResetableHours(e.target.value)}
																	error={
																		livePopInResetableHoursRequiredError
																			? i18n.t(
																					"portal.components.settings.institutionInformationTSX.popInResetableHoursRequired",
																				)
																			: livePopInResetableHoursValueError
																				? i18n.t(
																						"portal.components.settings.institutionInformationTSX.popInResetableHoursValueError",
																					)
																				: livePopInResetableHoursTooLargeError
																					? i18n.t(
																							"portal.components.settings.institutionInformationTSX.popInResetableHoursTooLargeError",
																						)
																					: undefined
																	}
																/>
															) : (
																<CopyOnlyTextField
																	id={id}
																	value={livePopInResetableHours}
																	copyButtonAriaLabel={i18n.t(
																		"portal.components.settings.institutionInformationTSX.popInResetableHoursAriaLabel",
																	)}
																/>
															)
														}
														isLoading={loading}
													/>
													<InfoRow
														header={i18n.t("portal.components.settings.institutionInformationTSX.popInUsage")}
														description={i18n.t(
															"portal.components.settings.institutionInformationTSX.popInUsageDescription",
														)}
														helperText={i18n.t("portal.components.settings.institutionInformationTSX.popInUsageHint")}
														createControl={(id) => (
															<CopyOnlyTextField
																id={id}
																value={livePopInUsage.toFixed(2)}
																copyButtonAriaLabel={i18n.t(
																	"portal.components.settings.institutionInformationTSX.popInUsageAriaLabel",
																)}
															/>
														)}
														isLoading={loading}
													/>
													<InfoRow
														header={i18n.t("portal.components.settings.institutionInformationTSX.popInHoursResetDate")}
														description={i18n.t(
															"portal.components.settings.institutionInformationTSX.popInHoursResetDateDescription",
														)}
														createControl={(id) => (
															<CoreDatePicker
																value={livePopInHoursResetDate}
																readOnly={!canEditVeritySettings}
																minDate={canEditVeritySettings ? minDate : undefined}
																onChange={
																	canEditVeritySettings
																		? (date) => setLivePopInHoursResetDate(date ?? undefined)
																		: undefined
																}
																slotProps={{
																	textField: { id, InputProps: { sx: { width: 165 } } },
																}}
																error={
																	canEditVeritySettings
																		? livePopInHoursResetDateRequiredError
																			? i18n.t(
																					"portal.components.settings.institutionInformationTSX.popInHoursResetDateRequired",
																				)
																			: livePopInHoursResetDatePastError
																				? i18n.t(
																						"portal.components.settings.institutionInformationTSX.popInHoursResetDatePast",
																					)
																				: undefined
																		: undefined
																}
															/>
														)}
														isLoading={loading}
													/>
													<InfoRow
														header={i18n.t("portal.components.settings.institutionInformationTSX.popInOneTimeHours")}
														description={i18n.t(
															"portal.components.settings.institutionInformationTSX.popInOneTimeHoursDescription",
														)}
														helperText={i18n.t(
															"portal.components.settings.institutionInformationTSX.popInOneTimeHoursHint",
														)}
														createControl={(id) =>
															canEditVeritySettings ? (
																<CoreTextField
																	id={id}
																	value={livePopInOneTimeHours}
																	type="number"
																	autoComplete="off"
																	fullWidth
																	onChange={(e) => setLivePopInOneTimeHours(e.target.value)}
																	error={
																		livePopInOneTimeHoursValueError
																			? i18n.t(
																					"portal.components.settings.institutionInformationTSX.popInOneTimeHoursError",
																				)
																			: undefined
																	}
																/>
															) : (
																<CopyOnlyTextField
																	id={id}
																	value={livePopInOneTimeHours}
																	copyButtonAriaLabel={i18n.t(
																		"portal.components.settings.institutionInformationTSX.popInOneTimeHoursAriaLabel",
																	)}
																/>
															)
														}
														isLoading={loading}
													/>
												</>
											)}
									</>
								)}
							</table>
						</div>
					</PageCard>
				</div>
				{canEditVeritySettings && initialVerityInstituteSettings?.allowDemoMode && (
					<DemoCourseCard
						cleanupEnabled={cleanupEnabled}
						cleanupFrequency={cleanupFrequency}
						loading={loading}
						cutoffDate={cutoffDate}
						setCleanupEnabled={setCleanupEnabled}
						setCleanupFrequency={setCleanupFrequency}
						setCutoffDate={setCutoffDate}
					/>
				)}
			</div>
		</ThemeWrapper>
	);
}
